import api from './api';
import { User } from '../types';

/** Opleider onboarding-wizard (/api/opleider/onboarding, migratie 149). */

export type OpleiderOnboardingStatus = 'PENDING_ONBOARDING' | 'REJECTED_SBI' | 'ONBOARDED_ACTIVE';
export type OpleiderStap = 1 | 2 | 3 | 4 | 5 | 'actief' | 'geweigerd';
export type Certificering = 'NRTO' | 'CEDEO' | 'CRKBO';

export interface KvkSbiActiviteit {
  code: string;
  omschrijving: string;
  hoofdactiviteit: boolean;
}

export interface OpleiderOnboardingState {
  status: OpleiderOnboardingStatus;
  stap: OpleiderStap;
  email: string;
  email_verified: boolean;
  stripe_status: 'not_started' | 'pending' | 'verified' | 'rejected';
  provider: {
    organisatie_naam: string | null;
    contactpersoon: string;
    telefoon: string | null;
    logo_url: string | null;
    omslag_url: string | null;
    bio_html: string | null;
    certificeringen: Certificering[];
    kvk_nummer: string | null;
    btw_nummer: string | null;
    postcode: string | null;
    stad: string | null;
    kvk_handelsnaam: string | null;
    kvk_sbi_codes: KvkSbiActiviteit[];
    sbi_matched: string[];
    contract_versie: string | null;
    contract_signed_at: string | null;
  };
  sbi_witte_lijst: Record<string, string>;
  eisen: { logo_min_px: number; omslag_min_breedte: number; omslag_min_ratio: number };
}

export interface OpleiderContract {
  versie: string;
  titel: string;
  html: string;
  matrix_verklaring: string;
  hash: string;
}

export interface OpleiderAccountData {
  email: string;
  password: string;
  password_confirm: string;
  contactpersoon: string;
  telefoon: string;
}

export interface OpleiderKvkData {
  organisatie_naam: string;
  postcode: string;
  plaats: string;
  kvk_nummer: string;
  btw_nummer: string;
}

export const registreerOpleider = async (data: OpleiderAccountData): Promise<{ user: User; token: string }> => {
  const res = await api.post('/opleider/onboarding/register', data);
  return res.data.data;
};

export const getOnboardingState = async (sync = false): Promise<OpleiderOnboardingState> => {
  const res = await api.get('/opleider/onboarding', { params: sync ? { sync: 1 } : undefined });
  return res.data.data;
};

export const verstuurVerificatieOpnieuw = async (): Promise<void> => {
  await api.post('/opleider/onboarding/resend-verification');
};

export const slaProfielOp = async (data: {
  logo: File | null;
  omslag: File | null;
  bio_html: string;
  certificeringen: Certificering[];
}): Promise<void> => {
  const fd = new FormData();
  if (data.logo) fd.append('logo', data.logo);
  if (data.omslag) fd.append('omslag', data.omslag);
  fd.append('bio_html', data.bio_html);
  fd.append('certificeringen', JSON.stringify(data.certificeringen));
  await api.post('/opleider/onboarding/profiel', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};

export const controleerKvk = async (data: OpleiderKvkData) => {
  const res = await api.post('/opleider/onboarding/kvk', data);
  return res.data.data as { handelsnaam: string | null; sbi_matched: string[]; sbi_codes: KvkSbiActiviteit[] };
};

export const getOpleiderContract = async (): Promise<OpleiderContract> => {
  const res = await api.get('/opleider/onboarding/contract');
  return res.data.data;
};

export const tekenOpleiderContract = async (data: {
  getypte_naam: string;
  matrix_akkoord: boolean;
  volledig_gelezen: boolean;
  contract_hash: string;
}): Promise<void> => {
  await api.post('/opleider/onboarding/contract/sign', data);
};

/** Foutmelding uit een axios-fout (server stuurt { message }). */
export function foutMelding(err: unknown, fallback: string): string {
  const data = (err as { response?: { data?: { message?: string } } })?.response?.data;
  return data?.message || fallback;
}
